import React from 'react';

interface LogoMarkProps {
  className?: string;
  /** When true, the orbital dot continuously rotates around the core. */
  animated?: boolean;
}

/**
 * Spawncore "orbital" mark.
 * Violet core ring + cyan orbital spawn-point. Pure SVG; scales crisp at any size.
 */
export const LogoMark: React.FC<LogoMarkProps> = ({ className, animated = true }) => (
  <svg
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    {/* Outer ring */}
    <circle
      cx="32"
      cy="32"
      r="21"
      stroke="hsl(var(--primary))"
      strokeWidth="4.5"
    />
    {/* Inner core dot */}
    <circle cx="32" cy="32" r="6.5" fill="hsl(var(--primary))" />

    {/* Orbital cyan dot — continuously orbits the ring */}
    <g
      className={animated ? 'animate-orbit-spin' : undefined}
      style={{ transformOrigin: '32px 32px' }}
    >
      <circle cx="50.5" cy="19.5" r="7" fill="hsl(var(--lime))" />
      <circle cx="50.5" cy="19.5" r="2.4" fill="hsl(var(--background))" />
    </g>
  </svg>
);

interface WordmarkProps {
  size?: 'sm' | 'md' | 'lg';
  showSubtitle?: boolean;
  className?: string;
}

export const Wordmark: React.FC<WordmarkProps> = ({
  size = 'md',
  showSubtitle = true,
  className = '',
}) => {
  const sizes = {
    sm: { mark: 'h-7 w-7', word: 'text-base', sub: 'text-[9px] tracking-[0.28em]' },
    md: { mark: 'h-9 w-9', word: 'text-xl',    sub: 'text-[10px] tracking-[0.28em]' },
    lg: { mark: 'h-11 w-11', word: 'text-2xl', sub: 'text-[11px] tracking-[0.3em]' },
  }[size];

  return (
    <div className={`group inline-flex items-center gap-3 ${className}`}>
      <LogoMark className={`${sizes.mark} flex-shrink-0`} />
      <div className="flex flex-col leading-tight">
        <span className={`font-brand ${sizes.word} text-foreground`}>
          Spawncore
        </span>
        {showSubtitle && (
          <span className={`font-mono-tag ${sizes.sub} uppercase text-muted-foreground`}>
            Web · AI Studio
          </span>
        )}
      </div>
    </div>
  );
};

export default Wordmark;
